import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Typography,
  Modal,
  Space,
  Descriptions,
  Avatar,
  Badge,
  Tag,
  notification,
  Image,
} from "antd";
import {
  DeleteTwoTone,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LockOutlined,
  StopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const { Text, Paragraph } = Typography;
const { confirm } = Modal;

const getEtablissementName = (report) =>
  report?.postId?.etablissement?.nameEtab ||
  report?.postId?.etablissement?.title ||
  report?.postId?.etablissement?.name ||
  "N/A";

const getReportState = (report) => {
  if (report?.userBanned) {
    return { color: "red", label: "Profil banni" };
  }

  if (report?.rewardsFrozen) {
    return { color: "orange", label: "Recompenses gelees" };
  }

  return { color: "blue", label: "En attente" };
};

const Signalement = () => {
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || '{"type":"admin"}'),
    []
  );
  const [data, setData] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const handleRefetch = () => {
    setRefetch((current) => !current);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const url =
        user?.type === "admin"
          ? `${process.env.REACT_APP_API_URL}/Reports`
          : `${process.env.REACT_APP_API_URL}/Reports/ettab/${user?.IdEtab}`;

      const response = await axios.get(url);
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      notification.error({
        message: "Erreur de chargement des donnees",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [refetch]);

  const showDeleteConfirm = (reportData) => {
    confirm({
      title: "Voulez-vous supprimer ce signalement ?",
      icon: <ExclamationCircleOutlined />,
      async onOk() {
        setIsLoading(true);
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/Reports/${reportData._id}`
          );
          notification.success({
            message: "Signalement supprime avec succes",
          });
          handleRefetch();
        } catch (error) {
          notification.error({
            message: "Erreur lors de la suppression",
            description: error.message,
          });
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const handleModerationAction = async (action) => {
    if (!selectedReport?._id) {
      return;
    }

    setActionLoading(action);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/Reports/${selectedReport._id}/${action}`,
        {
          actorType: user?.type || "admin",
        }
      );

      setSelectedReport(response.data);
      notification.success({
        message:
          action === "freeze-rewards"
            ? "Recompenses gelees"
            : "Profil banni",
        description:
          action === "freeze-rewards"
            ? "Les recompenses liees a ce post sont maintenant indisponibles."
            : "Le profil mobile est banni et ses recompenses sont bloquees.",
      });
      handleRefetch();
    } catch (error) {
      notification.error({
        message: "Action impossible",
        description:
          error?.response?.data?.message || error.message || "Erreur inconnue",
      });
    } finally {
      setActionLoading("");
    }
  };

  const columns = [
    {
      title: "Etablissement",
      key: "etablissement",
      render: (_, record) => (
        <Space>
          <Avatar
            size="small"
            src={
              record.postId?.etablissement?.avatar
                ? `${process.env.REACT_APP_API_URL}/upload/${record.postId?.etablissement?.avatar}`
                : null
            }
            icon={<UserOutlined />}
          />
          <Text strong>{getEtablissementName(record)}</Text>
        </Space>
      ),
    },
    {
      title: "Utilisateur app",
      key: "username",
      render: (_, record) =>
        record.postId?.userId?.username || record.postId?.username || "N/A",
    },
    {
      title: "Instagram",
      key: "instagram",
      render: (_, record) =>
        record.postId?.userId?.usernameInsta || record.postId?.username || "N/A",
    },
    {
      title: "Raison",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => <Text type="danger">{text}</Text>,
    },
    {
      title: "Statut post",
      key: "status",
      render: (_, record) => (
        <Badge
          status={record.postId?.status === "Valid" ? "success" : "error"}
          text={record.postId?.status || "N/A"}
        />
      ),
    },
    {
      title: "Moderation",
      key: "moderationState",
      render: (_, record) => {
        const state = getReportState(record);
        return <Tag color={state.color}>{state.label}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            onClick={() => {
              setSelectedReport(record);
              setIsModalOpen(true);
            }}
          >
            <InfoCircleOutlined /> Details
          </Button>
          <Button danger onClick={() => showDeleteConfirm(record)}>
            <DeleteTwoTone twoToneColor="red" />
          </Button>
        </Space>
      ),
    },
  ];

  const selectedState = getReportState(selectedReport);

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            loading={isLoading}
            className="criclebox tablespace mb-24"
            title="Gestion des signalements"
            extra={
              <Button type="primary" onClick={handleRefetch}>
                Rafraichir
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={data}
              pagination={{ pageSize: 10 }}
              rowKey="_id"
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Details du signalement"
        visible={isModalOpen}
        onCancel={closeModal}
        width={820}
        footer={[
          <Button key="close" onClick={closeModal}>
            Fermer
          </Button>,
          <Button
            key="freeze"
            icon={<LockOutlined />}
            loading={actionLoading === "freeze-rewards"}
            disabled={!!selectedReport?.rewardsFrozen || !!selectedReport?.userBanned}
            onClick={() => handleModerationAction("freeze-rewards")}
          >
            Geler les recompenses
          </Button>,
          user?.type === "admin" ? (
            <Button
              key="ban"
              danger
              icon={<StopOutlined />}
              loading={actionLoading === "ban-user"}
              disabled={!!selectedReport?.userBanned}
              onClick={() => handleModerationAction("ban-user")}
            >
              Bannir le profil
            </Button>
          ) : null,
        ]}
      >
        <Descriptions title="Signalement" bordered column={1}>
          <Descriptions.Item label="Raison">
            <Text type="danger" strong>
              {selectedReport?.description || "N/A"}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Statut de moderation">
            <Tag color={selectedState.color}>{selectedState.label}</Tag>
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="Post signale"
          bordered
          column={2}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Utilisateur app">
            {selectedReport?.postId?.userId?.username ||
              selectedReport?.postId?.username ||
              "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Instagram">
            {selectedReport?.postId?.userId?.usernameInsta ||
              selectedReport?.postId?.username ||
              "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            <Badge
              status={selectedReport?.postId?.status === "Valid" ? "success" : "error"}
              text={selectedReport?.postId?.status || "N/A"}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Lien du post">
            {selectedReport?.postId?.url ? (
              <a
                href={selectedReport?.postId?.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ouvrir le post Instagram
              </a>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Likes">
            {selectedReport?.postId?.likes || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Commentaires">
            {selectedReport?.postId?.comments || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: "plus" }}>
              {selectedReport?.postId?.description || "N/A"}
            </Paragraph>
          </Descriptions.Item>
          <Descriptions.Item label="Media" span={2}>
            {selectedReport?.postId?.mediaUrl ? (
              <Image
                width={200}
                src={`${process.env.REACT_APP_API_URL}/upload/${selectedReport?.postId?.mediaUrl}`}
              />
            ) : (
              "Aucun media"
            )}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="Etablissement"
          bordered
          column={2}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Nom">
            {getEtablissementName(selectedReport)}
          </Descriptions.Item>
          <Descriptions.Item label="Ville">
            {selectedReport?.postId?.etablissement?.ville || "N/A"}{" "}
            {selectedReport?.postId?.etablissement?.codePostal || ""}
          </Descriptions.Item>
          <Descriptions.Item label="Adresse" span={2}>
            {selectedReport?.postId?.etablissement?.location || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: "plus" }}>
              {selectedReport?.postId?.etablissement?.description || "N/A"}
            </Paragraph>
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </div>
  );
};

export default Signalement;
